import { useState } from "react";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import {
  FeeWriteRequest,
  useAddFee,
  useDeleteFee,
  useFeeList,
  useUpdateFee,
} from "src/dm/hooks/useDealFees";
import { DealFeeEntry } from "src/dm/interfaces/DealActivity";

// Legacy ViewFees has three distinct Add Allocation buttons: Ad, Admin,
// FlySheet. We mirror that exactly — three buttons, each pre-fills the
// fee-type field on the Add dialog.
const FEE_TYPES = [
  { code: "AD", label: "Ad Fee" },
  { code: "ADMIN", label: "Admin Fee" },
  { code: "FLYSHEET", label: "FlySheet Fee" },
] as const;

const emptyDraft = (feeType: string): FeeWriteRequest => ({
  feeType,
});

const DealFeesPage = () => {
  const deal = useDealContext();
  const { data, isLoading, isError, error } = useFeeList(deal.dealId);
  const add = useAddFee(deal.dealId);
  const update = useUpdateFee(deal.dealId);
  const remove = useDeleteFee(deal.dealId);

  const [addType, setAddType] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState<FeeWriteRequest>(emptyDraft(""));
  const [editRow, setEditRow] = useState<DealFeeEntry | null>(null);

  const startAdd = (feeType: string) => {
    setAddType(feeType);
    setAddDraft(emptyDraft(feeType));
  };

  const handleAdd = async () => {
    await add.mutateAsync(addDraft);
    setAddType(null);
  };

  const handleEdit = async () => {
    if (!editRow) return;
    await update.mutateAsync({
      feeId: editRow.feeId,
      feeType: editRow.feeType,
      body: {
        feeType: editRow.feeType,
        supportTeamMember: editRow.supportTeamMember,
        apVendorNumber: editRow.apVendorNumber,
        businessUnit: editRow.businessUnit,
        addressSequence: editRow.addressSequence,
        entryType: editRow.entryType,
        invoiceDescription: editRow.invoiceDescription,
        feeAmount: editRow.feeAmount,
      },
    });
    setEditRow(null);
  };

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as DealFeeEntry;
    return (
      <td>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          onClick={() => setEditRow({ ...row })}
        >
          Edit
        </Button>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          themeColor="error"
          onClick={() => {
            if (confirm(`Delete fee #${row.feeId} (${row.feeType})?`))
              remove.mutate({ feeId: row.feeId, feeType: row.feeType });
          }}
          disabled={remove.isPending}
        >
          Delete
        </Button>
      </td>
    );
  };

  // Group totals — legacy shows allocated vs unallocated by fee type at the top.
  const totals = (FEE_TYPES as readonly { code: string; label: string }[]).map((ft) => {
    const rows = (data ?? []).filter((r) => r.feeType === ft.code);
    const total = rows.reduce((sum, r) => sum + (r.feeAmount ?? 0), 0);
    return { ...ft, count: rows.length, total };
  });

  if (isLoading) return <p>Loading fees…</p>;
  if (isError)
    return <p style={{ color: "#b91c1c" }}>{error instanceof Error ? error.message : "Failed to load"}</p>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <h2 style={{ margin: 0 }}>Fees</h2>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {FEE_TYPES.map((ft) => (
            <Button
              key={ft.code}
              type="button"
              themeColor="primary"
              onClick={() => startAdd(ft.code)}
            >
              Add {ft.label}
            </Button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", marginBottom: "0.75rem" }}>
        {totals.map((t) => (
          <div
            key={t.code}
            style={{ background: "#fff", padding: "0.75rem 1rem", borderRadius: "6px", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}
          >
            <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase" }}>{t.label}</div>
            <div style={{ fontSize: "1rem", color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
              {t.count} row{t.count === 1 ? "" : "s"} · ${t.total.toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      {(data?.length ?? 0) === 0 ? (
        <p style={{ color: "#94a3b8" }}>No fees on this deal.</p>
      ) : (
        <Grid data={data}>
          <GridColumn field="feeId" title="#" width="60px" />
          <GridColumn field="feeType" title="Type" width="100px" />
          <GridColumn field="apVendorNumber" title="AP Vendor" width="140px" />
          <GridColumn field="businessUnit" title="BU" width="80px" />
          <GridColumn field="addressSequence" title="Addr" width="70px" />
          <GridColumn field="entryType" title="Entry" width="100px" />
          <GridColumn field="feeAmount" title="Amount" width="110px" format="{0:c2}" />
          <GridColumn field="processStatus" title="Status" width="80px" />
          <GridColumn field="invoiceDescription" title="Invoice Description" />
          <GridColumn title="" width="150px" cells={{ data: ActionsCell }} />
        </Grid>
      )}

      {addType && (
        <Dialog
          title={`Add ${FEE_TYPES.find((f) => f.code === addType)?.label}`}
          onClose={() => setAddType(null)}
        >
          <FeeFormFields draft={addDraft} setDraft={setAddDraft} />
          <DialogActionsBar>
            <Button onClick={() => setAddType(null)}>Cancel</Button>
            <Button themeColor="primary" onClick={handleAdd} disabled={add.isPending}>
              {add.isPending ? "Saving…" : "Add"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {editRow && (
        <Dialog title={`Edit fee #${editRow.feeId}`} onClose={() => setEditRow(null)}>
          <FeeFormFields
            draft={{
              feeType: editRow.feeType,
              supportTeamMember: editRow.supportTeamMember,
              apVendorNumber: editRow.apVendorNumber,
              businessUnit: editRow.businessUnit,
              addressSequence: editRow.addressSequence,
              entryType: editRow.entryType,
              invoiceDescription: editRow.invoiceDescription,
              feeAmount: editRow.feeAmount,
            }}
            setDraft={(d) => {
              const draft = typeof d === "function" ? (d as (p: FeeWriteRequest) => FeeWriteRequest)(editRow as unknown as FeeWriteRequest) : d;
              setEditRow({
                ...editRow,
                supportTeamMember: draft.supportTeamMember ?? null,
                apVendorNumber: draft.apVendorNumber ?? null,
                businessUnit: draft.businessUnit ?? null,
                addressSequence: draft.addressSequence ?? null,
                entryType: draft.entryType ?? null,
                invoiceDescription: draft.invoiceDescription ?? null,
                feeAmount: draft.feeAmount ?? null,
              });
            }}
          />
          <DialogActionsBar>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button themeColor="primary" onClick={handleEdit} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

interface FormFieldsProps {
  draft: FeeWriteRequest;
  setDraft: React.Dispatch<React.SetStateAction<FeeWriteRequest>>;
}
const FeeFormFields = ({ draft, setDraft }: FormFieldsProps) => {
  const setF = <K extends keyof FeeWriteRequest>(k: K, v: FeeWriteRequest[K]) =>
    setDraft((p) => ({ ...p, [k]: v }));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", padding: "0.25rem 0" }}>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>AP Vendor #</span>
        <Input
          value={draft.apVendorNumber ?? ""}
          onChange={(e) => setF("apVendorNumber", String(e.target.value ?? "") || null)}
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Business Unit</span>
        <Input
          value={draft.businessUnit ?? ""}
          onChange={(e) => setF("businessUnit", String(e.target.value ?? "") || null)}
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Address Seq</span>
        <NumericTextBox
          value={draft.addressSequence ?? null}
          onChange={(e) => setF("addressSequence", e.value ?? null)}
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Entry Type</span>
        <Input
          value={draft.entryType ?? ""}
          onChange={(e) => setF("entryType", String(e.target.value ?? "") || null)}
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Support Team Mbr</span>
        <Input
          value={draft.supportTeamMember ?? ""}
          onChange={(e) => setF("supportTeamMember", String(e.target.value ?? "") || null)}
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Amount</span>
        <NumericTextBox
          format="c2"
          value={draft.feeAmount ?? null}
          onChange={(e) => setF("feeAmount", e.value ?? null)}
        />
      </label>
      <label style={{ gridColumn: "1 / -1" }}>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>Invoice Description</span>
        <Input
          value={draft.invoiceDescription ?? ""}
          onChange={(e) => setF("invoiceDescription", String(e.target.value ?? "") || null)}
        />
      </label>
    </div>
  );
};

export default DealFeesPage;
